// ---------------------------------------------------------------------------
// InstancedMesh scatter (#3). Still fully procedural — same code-generated
// shapes as before, just drawn far more cheaply. Instead of thousands of
// Groups (a draw call each), we:
//   1. bake each deco's build() into merged, vertex-colored geometry — one
//      merged half per toon ramp (chunky cel vs the rounded prims' smooth
//      ramp, plan 28 stage 4), since a merged mesh has ONE gradient map,
//   2. pre-bake a handful of seeded VARIANTS per type for visual variety,
//   3. draw all instances of a (type, variant) as a single InstancedMesh
//      (two, when the deco mixes chunky + rounded parts).
// Per-instance position/rotation/scale ride the instance matrix; sway is done
// in a tiny vertex-shader injection so animating it costs nothing on the CPU.
// ---------------------------------------------------------------------------
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { TOON_GRADIENT, TOON_SMOOTH, toonify } from "./toon.ts";
import { rand } from "./color.ts";
import { makePrims, SHARED_GEOS } from "./meshkit.ts";
import type { DecoSpec, DecoBuildCtx } from "./deco/define.ts";

/** Distinct pre-baked shapes per deco type — instances pick one for variety. */
export const VARIANTS = 8;

/** Shared wind clock; game.ts advances .value each frame. */
export const SWAY_TIME = { value: 0 };

/** Give a geometry a flat per-vertex color and drop uv (so all parts merge cleanly). */
/** Bake a solid color into a vertex-color attribute (and drop uv — toon
 *  materials sample no maps), so differently-colored parts can merge under ONE
 *  vertex-colored material. Shared with the character-rig merge (npc/define.ts). */
export function paint(geo: THREE.BufferGeometry, color: number): THREE.BufferGeometry {
  const c = new THREE.Color(color);
  const n = geo.attributes.position.count;
  const arr = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    arr[i * 3] = c.r;
    arr[i * 3 + 1] = c.g;
    arr[i * 3 + 2] = c.b;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(arr, 3));
  geo.deleteAttribute("uv");
  return geo;
}

/** Flatten one pushed prim mesh into a transform-baked, vertex-colored
 *  geometry — the shared inner step of bakeDeco below AND the character-rig
 *  merge (npc/define.ts mergeRigidParts), so the flatten/paint rules can't
 *  drift between decos and rigs. `copy: true` always returns a geometry the
 *  caller owns and never disposes the source (the RIG path: mirror/repeat part
 *  modifiers reuse ONE geometry across meshes, so an in-place transform would
 *  double-apply on the second user — the caller reference-counts disposal);
 *  `copy: false` mutates a fresh non-indexed original in place and disposes an
 *  indexed source (the DECO path: every geometry is built fresh per bake). The
 *  process-shared unit geos (SHARED_GEOS: boxes, sharp, cyl, sphere) are never
 *  mutated or disposed on either path. */
export function bakePrim(m: THREE.Mesh, copy: boolean): THREE.BufferGeometry {
  const src = m.geometry;
  const sharedGeo = SHARED_GEOS.has(src);
  // Indexed shared geos (UNIT_CYL / UNIT_SPHERE) must clone via toNonIndexed —
  // never dispose the process-shared source.
  const g = src.index ? src.toNonIndexed() : copy || sharedGeo ? src.clone() : src;
  if (!copy && g !== src && !sharedGeo) src.dispose();
  m.updateMatrix();
  g.applyMatrix4(m.matrix); // applyMatrix4 transforms + renormalizes normals correctly
  paint(g, (m.material as THREE.MeshToonMaterial).color.getHex());
  return g;
}

/** The baked halves of one deco build. A merged geometry can carry only ONE
 *  gradient map, so parts on the chunky cel ramp (box/cone/…, toonMat) and
 *  parts on the smooth curved-prim ramp (ball/bone/lathe/arc, toonMatSmooth)
 *  merge separately — a mixed deco draws as two InstancedMeshes per bucket. */
type BakedDeco = { chunky: THREE.BufferGeometry | null; smooth: THREE.BufferGeometry | null };

/** Run a deco's build() but bake the primitives into merged geometry — through
 *  the REAL prim kit (the same makePrims buildDeco uses), so the instanced
 *  scatter can never drift from the Group path (rot handling, every rounded
 *  prim, future prims — all inherited). Each pushed mesh is flattened into
 *  vertex-colored geometry via its own matrix and bucketed by the toon ramp
 *  its material samples. */
function bakeDeco(spec: DecoSpec, seed: number): BakedDeco {
  const chunky: THREE.BufferGeometry[] = [];
  const smooth: THREE.BufferGeometry[] = [];

  const bake = (m: THREE.Mesh) => {
    if (!m.isMesh) return; // limb()/socket() push Groups — not in the deco ctx
    const g = bakePrim(m, false); // deco geometry is fresh per call — bake in place
    const mat = m.material as THREE.MeshToonMaterial;
    (mat.gradientMap === TOON_SMOOTH ? smooth : chunky).push(g);
  };

  const ctx: DecoBuildCtx = { seed, ...makePrims(bake, seed) };
  spec.build(ctx);

  const mergeAll = (parts: THREE.BufferGeometry[]): THREE.BufferGeometry | null => {
    if (parts.length === 0) return null;
    const merged = mergeGeometries(parts, false)!;
    parts.forEach((p) => p.dispose());
    return merged;
  };
  return { chunky: mergeAll(chunky), smooth: mergeAll(smooth) };
}

// Baked geometry cached per (type, variant); materials cached per (type, ramp).
const geoCache = new Map<string, BakedDeco>();
function bakedGeo(spec: DecoSpec, variant: number): BakedDeco {
  const key = `${spec.type}:${variant}`;
  let g = geoCache.get(key);
  if (!g) {
    g = bakeDeco(spec, variant * 1013 + 7);
    geoCache.set(key, g);
  }
  return g;
}

const matCache = new Map<string, THREE.MeshToonMaterial>();
function instMat(spec: DecoSpec, half: keyof BakedDeco): THREE.MeshToonMaterial {
  const key = `${spec.type}:${half}`;
  let m = matCache.get(key);
  if (m) return m;
  m = new THREE.MeshToonMaterial({
    vertexColors: true,
    gradientMap: half === "smooth" ? TOON_SMOOTH : TOON_GRADIENT,
  });
  if (spec.sway) {
    // Wind sway in the vertex shader: bend +x by sin(time + per-instance phase),
    // scaled by height so the tip moves and the base stays planted.
    const sway = spec.sway;
    m.onBeforeCompile = (shader) => {
      shader.uniforms.uTime = SWAY_TIME;
      shader.uniforms.uSway = { value: sway };
      shader.vertexShader =
        "uniform float uTime;\nuniform float uSway;\nattribute float aPhase;\n" +
        shader.vertexShader.replace(
          "#include <begin_vertex>",
          "#include <begin_vertex>\n\ttransformed.x += sin(uTime * 1.5 + aPhase) * uSway * max(transformed.y, 0.0);",
        );
    };
    // Constant key, NOT the magnitude: the injected GLSL is byte-identical for
    // every sway value (the strength is the uSway UNIFORM, per-material) — a
    // per-magnitude key was compiling one redundant program per distinct sway
    // value (~8 across content/decos, ×2 ramp halves) for the same shader text.
    m.customProgramCacheKey = () => "sway1";
  }
  // Rim light composes OVER the sway hook (toonify chains the previous
  // onBeforeCompile and extends the cache key) — order matters: sway first.
  toonify(m);
  matCache.set(key, m);
  return m;
}

export type Placement = {
  spec: DecoSpec;
  x: number; y: number; z: number;
  rot: number;
  scale: number;
  variant: number;
  /** Interactable id (set by buildWorld for decos with an `interact` block) —
   *  enables click-picking (pickInstanced) and hiding (setDecoHidden). */
  id?: string;
};

// ---------------------------------------------------------------------------
// View-distance refresh (Phase C of knowledge-base/plans/02). The scatter's instances span the
// whole map, so a per-mesh frustum test is useless with a whole-map bounding
// sphere — and the fog fully hides everything past 135 anyway. Instead each
// mesh keeps its FULL placement set on the CPU (positions + baked matrices) and
// `updateDecoView` rewrites the live instance buffer with only the placements
// within VIEW_RADIUS of the player, shrinking the bounding sphere to match so
// regular frustum culling works again. The refresh runs only after the player
// has moved REFRESH_MOVE units — a few thousand distance tests + a small buffer
// upload, nothing per-frame.
//
// VIEW_RADIUS is player-distance; the camera sits ≤ ~21 behind the player and
// fog is camera-distance. Worst case an instance pops at 170 − 12 (stale
// refresh) − 21 (camera offset) = 137 from the camera — past full fog (135).
const VIEW_RADIUS = 170;
const REFRESH_MOVE = 12;

type ViewData = {
  xs: Float32Array;            // placement positions, for the distance test
  zs: Float32Array;
  mats: Float32Array;          // ALL baked instance matrices (n×16)
  phases: Float32Array | null; // per-instance sway phase, reordered with mats
  margin: number;              // model extent → bounding-sphere padding
  /** Interactable id per FULL placement index (null bucket = no interactables). */
  ids: (string | undefined)[] | null;
  /** 1 = instance suppressed (picked flower awaiting respawn) — skipped by the
   *  view refresh so it vanishes from the live buffer. */
  hidden: Uint8Array;
  /** live (compacted) slot k → FULL placement index. Raycast hits report the
   *  live instanceId; this maps it back to a placement/id. Identity until the
   *  first refresh (headless/studio never refresh — full order = live order). */
  liveSrc: Uint32Array;
};

// Every InstancedMesh managed by updateDecoView. Rebuilt per buildInstanced
// call (one world build), so an HMR world rebuild doesn't accumulate stale meshes.
const viewMeshes: THREE.InstancedMesh[] = [];

/** A non-instanced scene object culled purely by distance (cabins/wells — real
 *  mesh Groups; hiding the group prunes its whole subtree from traversal). */
export type CulledDeco = { obj: { visible: boolean }; x: number; z: number; r: number };
const culledExtras: CulledDeco[] = [];

/** Register the distance-culled extras (world.userData.culledDecos). Client-only —
 *  the headless server never calls this (or updateDecoView). */
export function registerCulled(list: CulledDeco[]): void {
  culledExtras.length = 0;
  culledExtras.push(...list);
}

let lastX = Infinity, lastZ = Infinity;

/** Refresh which deco instances are live, once the player has moved enough.
 *  Call every frame from the game loop; it early-outs to a distance check.
 *  Returns true when it actually rewrote the instance buffers — callers with
 *  baked shadow maps (the night lamps) use that to schedule a re-bake, since
 *  the scatter is a shadow caster and its live set just changed. */
export function updateDecoView(px: number, pz: number): boolean {
  const mx = px - lastX, mz = pz - lastZ;
  if (mx * mx + mz * mz < REFRESH_MOVE * REFRESH_MOVE) return false;
  lastX = px; lastZ = pz;

  const R2 = VIEW_RADIUS * VIEW_RADIUS;
  for (const mesh of viewMeshes) {
    const d = mesh.userData.view as ViewData;
    const live = mesh.instanceMatrix.array as Float32Array;
    const phaseAttr = d.phases
      ? (mesh.geometry.getAttribute("aPhase") as THREE.InstancedBufferAttribute)
      : null;
    let k = 0;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (let i = 0; i < d.xs.length; i++) {
      if (d.hidden[i]) continue; // suppressed (picked) — leave it out of the live set
      const x = d.xs[i]!, z = d.zs[i]!;
      const dx = x - px, dz = z - pz;
      if (dx * dx + dz * dz > R2) continue;
      const so = i * 16, to = k * 16;
      for (let j = 0; j < 16; j++) live[to + j] = d.mats[so + j]!;
      if (phaseAttr) (phaseAttr.array as Float32Array)[k] = d.phases![i]!;
      d.liveSrc[k] = i;
      const y = d.mats[so + 13]!; // ty of the baked matrix
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
      k++;
    }
    mesh.count = k;
    mesh.visible = k > 0;
    if (k > 0) {
      mesh.instanceMatrix.needsUpdate = true;
      if (phaseAttr) phaseAttr.needsUpdate = true;
      // Mutate the OBJECT-level sphere: Frustum.intersectsObject prefers
      // mesh.boundingSphere for InstancedMesh, and would otherwise compute it
      // once from the first frame's live set and cache it forever — a stale
      // spawn-centered sphere makes nearby decos flicker out as the camera turns.
      const bs = mesh.boundingSphere!;
      bs.center.set((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2);
      bs.radius = Math.hypot(maxX - minX, maxY - minY, maxZ - minZ) / 2 + d.margin;
    }
  }

  for (const c of culledExtras) {
    const dx = c.x - px, dz = c.z - pz;
    const r = VIEW_RADIUS + c.r;
    c.obj.visible = dx * dx + dz * dz < r * r;
  }
  return true;
}

/** Build one InstancedMesh per (type, variant) bucket. Returns a Group of them. */
export function buildInstanced(placements: Placement[]): THREE.Group {
  const group = new THREE.Group();
  group.name = "instanced-decos";
  viewMeshes.length = 0;
  instIndex.clear();
  lastX = Infinity; lastZ = Infinity; // force a refresh on the first updateDecoView

  const buckets = new Map<string, Placement[]>();
  for (const p of placements) {
    const key = `${p.spec.type}:${p.variant}`;
    let arr = buckets.get(key);
    if (!arr) buckets.set(key, (arr = []));
    arr.push(p);
  }

  const dummy = new THREE.Object3D();
  for (const arr of buckets.values()) {
    const spec = arr[0]!.spec;
    const variant = arr[0]!.variant;
    const baked = bakedGeo(spec, variant);
    const n = arr.length;

    // Per-instance data — computed ONCE per bucket. A mixed chunky+smooth deco
    // draws as TWO meshes (one per toon ramp) that SHARE these arrays: same
    // placements → same live set, and the one shared `hidden` means
    // setDecoHidden suppresses both halves through either mesh's ViewData.
    // (updateDecoView's liveSrc writes are identical for both, so sharing is safe.)
    const xs = new Float32Array(n);
    const zs = new Float32Array(n);
    const phases = new Float32Array(n);
    const mats = new Float32Array(n * 16);
    const ids: (string | undefined)[] = new Array(n);
    let anyId = false;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (let i = 0; i < n; i++) {
      const p = arr[i]!;
      dummy.position.set(p.x, p.y, p.z);
      dummy.rotation.set(0, p.rot, 0);
      dummy.scale.setScalar(p.scale);
      dummy.updateMatrix();
      dummy.matrix.toArray(mats, i * 16);
      phases[i] = rand(p.x * 1.7 + p.z * 3.1) * Math.PI * 2;
      if (p.id) { ids[i] = p.id; anyId = true; }
      xs[i] = p.x; zs[i] = p.z;
      if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
      if (p.z < minZ) minZ = p.z; if (p.z > maxZ) maxZ = p.z;
      if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y;
    }
    const hidden = new Uint8Array(n);
    const liveSrc = new Uint32Array(n);
    for (let i = 0; i < n; i++) liveSrc[i] = i; // identity until the first refresh

    let first: THREE.InstancedMesh | null = null;
    for (const half of ["chunky", "smooth"] as const) {
      const bakedHalf = baked[half];
      if (!bakedHalf) continue;
      // Clone the cached geometry so this mesh can own its per-instance aPhase
      // and its own (refresh-shrunk) bounding sphere.
      const geo = bakedHalf.clone();
      const mesh = new THREE.InstancedMesh(geo, instMat(spec, half), n);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage); // rewritten per view refresh
      (mesh.instanceMatrix.array as Float32Array).set(mats);
      mesh.instanceMatrix.needsUpdate = true;
      if (spec.sway) {
        // The attribute gets a COPY: updateDecoView compacts it in place, and the
        // pristine `phases` array must survive as the source for later refreshes.
        const attr = new THREE.InstancedBufferAttribute(new Float32Array(phases), 1);
        attr.setUsage(THREE.DynamicDrawUsage);
        geo.setAttribute("aPhase", attr);
      }

      // Model extent (offset + radius, at max instance scale, + sway slack) pads
      // the placement-extent bounding sphere so no geometry pokes out of it. The
      // geometry keeps its true MODEL-LOCAL sphere (raycasts etc.); the sphere the
      // frustum test uses is the OBJECT-level mesh.boundingSphere, which we own:
      // seeding it here keeps three from computing + caching its own (it never
      // invalidates it, so a cached one would go stale on the first view refresh).
      geo.computeBoundingSphere();
      const mbs = geo.boundingSphere!;
      const margin = (mbs.center.length() + mbs.radius) * 1.3;
      // Start with a sphere over ALL instances (correct even if updateDecoView is
      // never called, e.g. headless); each refresh re-fits it to the live set.
      mesh.boundingSphere = new THREE.Sphere(
        new THREE.Vector3((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2),
        Math.hypot(maxX - minX, maxY - minY, maxZ - minZ) / 2 + margin,
      );
      mesh.frustumCulled = true; // the sphere tracks the LIVE instances, so this works

      mesh.userData.view = {
        xs, zs, mats,
        phases: spec.sway ? phases : null, margin,
        ids: anyId ? ids : null,
        hidden,
        liveSrc,
      } satisfies ViewData;
      viewMeshes.push(mesh);
      group.add(mesh);
      if (!first) first = mesh;
    }
    // The id index points at the bucket's first mesh; the shared `hidden`
    // array makes a hide through it cover the other half too.
    if (first && anyId) {
      for (let i = 0; i < n; i++) {
        const id = ids[i];
        if (id) instIndex.set(id, { mesh: first, i });
      }
    }
  }
  return group;
}

// ---------------------------------------------------------------------------
// Interactable support (src/interact.ts): scattered decos with an `interact`
// block carry an id on their Placement, letting the client pick one with the
// mouse and hide one the server marked picked. Both are id-keyed through
// `instIndex`, rebuilt with the meshes on every buildInstanced.
// ---------------------------------------------------------------------------

const instIndex = new Map<string, { mesh: THREE.InstancedMesh; i: number }>();

/** Suppress (or restore) one scattered instance by its interactable id — a
 *  picked flower vanishes for everyone until its respawn. Forces the next
 *  updateDecoView to rewrite the live buffers. Returns false for unknown ids. */
export function setDecoHidden(id: string, hidden: boolean): boolean {
  const e = instIndex.get(id);
  if (!e) return false;
  (e.mesh.userData.view as ViewData).hidden[e.i] = hidden ? 1 : 0;
  lastX = Infinity; lastZ = Infinity; // next updateDecoView call rebuilds the live sets
  return true;
}

/** True if that interactable instance is currently suppressed. */
export function isDecoHidden(id: string): boolean {
  const e = instIndex.get(id);
  return !!e && (e.mesh.userData.view as ViewData).hidden[e.i] === 1;
}

/** Raycast the LIVE instanced decos that carry interactable ids. Returns the
 *  nearest hit's id + distance, or null. The hit's instanceId indexes the
 *  compacted live buffer; liveSrc maps it back to the placement. */
export function pickInstanced(raycaster: THREE.Raycaster): { id: string; distance: number } | null {
  let best: { id: string; distance: number } | null = null;
  for (const mesh of viewMeshes) {
    const d = mesh.userData.view as ViewData;
    if (!d.ids || !mesh.visible) continue;
    for (const hit of raycaster.intersectObject(mesh, false)) {
      if (hit.instanceId === undefined) continue;
      const id = d.ids[d.liveSrc[hit.instanceId]!];
      if (!id) continue;
      if (!best || hit.distance < best.distance) best = { id, distance: hit.distance };
      break; // hits are sorted by distance — first valid one is this mesh's best
    }
  }
  return best;
}
